<template>
  <Content
    class="quick-quote"
    header-icon="file-alt"
    :header-label="t('quickQuote.quickQuote')"
    :loading="rateQuotes.loading"
    show-toolbar
    remove-card-wrapper
  >
    <template #toolbar>
      <div role="toolbar" class="flex justify-content-center p-3">
        <RygenButton
          mode="primary"
          :disabled="rateQuotes.loading || !selectedClient"
          :icon="PrimeIcons.DOLLAR"
          :label="t('quickQuote.getQuote')"
          :loading="rateQuotes.loading"
          juicy
          @click="onGetRates()"
        />
        <!-- ^ onGetRates() with parens so the click event isn't passed -->
      </div>
    </template>

    <template #secondaryContent>
      <QuickQuoteRateShop
        v-if="rateQuotes.data"
        :id="RATE_SHOP_ID"
        :mileage="mileage"
        :rate-shop="rateShop"
      />
    </template>

    <BlockUI :key="selectedClient?.naturalId" :blocked="!selectedClient">
      <QuickQuoteStopSection
        id="stops"
        v-model:destination="request.destination"
        v-model:origin="request.origin"
        :has-attempted-resolution-origin="hasAttemptedResolution.origin"
        :has-attempted-resolution-destination="
          hasAttemptedResolution.destination
        "
      />

      <QuickQuoteLineItemSection
        id="line-items"
        ref="lineItemSection"
        v-model="request.lineItems"
      />

      <div class="detail-section flex flex-gap-1 align-items-stretch">
        <div class="flex flex-column flex-gap-1 w-6">
          <Section
            id="accessorials"
            class="quick-quote-accessorials flex-grow-1 flex flex-column"
            :header="t('referenceData.accessorials', 2)"
          >
            <Card class="flex-grow-1">
              <template #content>
                <AccessorialsEdit
                  v-model="request.requestedAccessorials"
                  class="w-6"
                />
              </template>
            </Card>
          </Section>
        </div>
      </div>
    </BlockUI>

    <AddressValidationResultModal
      v-model:visible="addressValidation.showModal"
      :class="addressValidation.currentLocation"
      :validation-result="addressValidation.validationResult"
      @accept-corrected-address="onAcceptCorrection"
      @keep-original-address="onKeepOriginalAddress"
    />

    <ChangeDomainModal
      v-model:visible="isChangeDomainModalVisible"
      :selected-domain="selectedClient"
      :selectable-domain-types="[DomainType.Client]"
      :domain-options="clients.data"
      :header="t('common.selectParam', { param: t('clients.clients') })"
      position="center"
      closable
      @change-domain="onClientChange"
    >
      <template v-if="!!selectedClient" #message>
        <Message severity="warn" icon="pi pi-exclamation-triangle">
          {{
            t('common.selectingClientWillResetParam', {
              param: t('common.requests'),
            })
          }}
        </Message>
      </template>
    </ChangeDomainModal>
  </Content>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import axios from 'axios';
import isEqual from 'lodash/isEqual';
import type { Ref } from 'vue';
import {
  computed,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import {
  useScrollAnimations,
  useToast,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import {
  AddressValidationResult,
  DomainReference,
  DomainTree,
  DomainType,
  ErrorResponse,
  Loadable,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  EmbeddableLocation,
  QuickQuoteLineItem,
  QuickQuoteRequest,
  QuickQuoteShippingPoint,
  RateQuote,
  RateShop,
  RateShopStatus,
} from '@/model';
import { useClientStore } from '@/stores/Client';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useMessageStore } from '@/stores/Message';
import { useParentEntityStore } from '@/stores/ParentEntity';

import AccessorialsEdit from '@/components/accessorial/AccessorialsEdit.vue';
import QuickQuoteLineItemSection from '@/components/quickQuote/QuickQuoteLineItemSection.vue';
import QuickQuoteRateShop from '@/components/quickQuote/QuickQuoteRateShop.vue';
import QuickQuoteStopSection from '@/components/quickQuote/QuickQuoteStopSection.vue';
import AddressValidationResultModal from '@rygen/rygen-ui/components/address/AddressValidationResultModal.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import ClientAvatar from '@rygen/rygen-ui/components/client/ClientAvatar.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import ChangeDomainModal from '@rygen/rygen-ui/components/domain/ChangeDomainModal.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import BlockUI from 'primevue/blockui';
import Card from 'primevue/card';
import Message from 'primevue/message';

const RATE_SHOP_ID = 'rate-shop';
const OWNING_CLIENT_MESSAGE_KEY = 'quick-quote-owning-client-message';

type LocationKey = 'origin' | 'destination';

const { clientTreeMap, clients, loadClients } = useClientStore();
const { selectedDomain } = useCurrentUserStore();
const { t } = useI18n();
const { addMessage, removeMessage } = useMessageStore();
const { clearParentEntity, setParentEntity } = useParentEntityStore();
const router = useRouter();
const { scrollIntoView } = useScrollAnimations();
const toast = useToast();
const { uid } = useUid('quick-quote');

const request: Ref<QuickQuoteRequest> = ref(
  new QuickQuoteRequest({
    lineItems: [new QuickQuoteLineItem()],
    origin: new QuickQuoteShippingPoint({ shipDate: new Date() }),
  }),
);
const { setServerErrors, validate, v$ } = useValidations(request, {}, uid);

const lineItemSection = ref<InstanceType<typeof QuickQuoteLineItemSection>>();
const selectedClient: Ref<DomainTree | undefined> = ref();
const rateQuotes: Ref<Loadable<RateQuote[]>> = ref({ loading: false });
const isLoading = ref(false);
const isChangeDomainModalVisible = ref(false);
const mileage: Ref<number | undefined> = ref();

const hasAttemptedResolution: Ref<Partial<Record<LocationKey, boolean>>> = ref({
  origin: false,
  destination: false,
});

const addressValidationQueue: Ref<
  {
    locationKey: LocationKey;
    validationResult: AddressValidationResult;
  }[]
> = ref([]);
const addressValidation = ref({
  showModal: false,
  validationResult: new AddressValidationResult(),
  currentLocation: 'origin' as LocationKey,
});

const hasBeenValidated: Ref<Partial<Record<LocationKey, boolean>>> = ref({
  origin: false,
  destination: false,
});

const rateShop: Ref<Loadable<RateShop>> = computed(() => {
  let rateShopStatus: RateShopStatus;
  if (rateQuotes.value.error) {
    rateShopStatus = RateShopStatus.ERROR;
  } else if (rateQuotes.value.data) {
    rateShopStatus = RateShopStatus.COMPLETED;
  } else {
    rateShopStatus = RateShopStatus.IN_PROGRESS;
  }

  return {
    data: new RateShop({
      id: -1,
      rateQuotes: rateQuotes.value.data,
      status: rateShopStatus,
    }),
    loading: rateQuotes.value.loading,
    error: rateQuotes.value.error,
  };
});

const validateAllLocations = async (): Promise<boolean> => {
  addressValidationQueue.value = [];
  const checks: Promise<boolean>[] = [];

  if (
    !hasBeenValidated.value.origin &&
    request.value.origin?.location &&
    request.value.origin.location.locationId === undefined
  ) {
    checks.push(validateLocation(request.value.origin.location, 'origin'));
  }

  if (
    !hasBeenValidated.value.destination &&
    request.value.destination?.location &&
    request.value.destination.location.locationId === undefined
  ) {
    checks.push(
      validateLocation(request.value.destination.location, 'destination'),
    );
  }

  const results = await Promise.all(checks);

  if (addressValidationQueue.value.length) {
    processNextInQueue();
  }

  return results.every(Boolean);
};

const lineItemsAreValid = () =>
  request.value.lineItems.every(
    (item) =>
      item.quantity != null &&
      item.quantity >= 1 &&
      item.weight != null &&
      item.weight >= 1,
  );

const onGetRates = async (fromModal = false) => {
  if (!fromModal) {
    const [isValid, isLineItemSectionValid] = await Promise.all([
      validate(),
      lineItemSection.value?.validate(),
    ]);
    if (!isValid || !isLineItemSectionValid || !lineItemsAreValid()) {
      return;
    }
  }

  try {
    if (!(await validateAllLocations())) {
      return;
    }

    const incompleteStop = [
      request.value.origin,
      request.value.destination,
    ].find(
      (stop) =>
        !stop?.location?.postalCode ||
        !stop?.location?.city ||
        !stop?.location?.state,
    );

    if (incompleteStop) {
      await validate();
      return;
    }

    if (!lineItemsAreValid()) {
      await lineItemSection.value?.validate();
      return;
    }

    rateQuotes.value.loading = true;
    const response = await api.quickQuote.getRates(request.value);
    rateQuotes.value.data = response.rates;
    mileage.value = response.mileage;
    nextTick(() => {
      const rateShopEl = document.querySelector(
        `#${RATE_SHOP_ID}`,
      ) as HTMLElement;
      scrollIntoView(rateShopEl, 'start');
    });
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    const err = e as Error;
    const errorDetail = e.response?.data.exceptionMessage;
    if (axios.isAxiosError(err) && err.response?.status === 400) {
      const errorResponse = err.response.data as ErrorResponse;
      setServerErrors(errorResponse);
    } else {
      console.error('Error getting quick quote', request.value, err);
      toast.addError({
        detail:
          t('quickQuote.errors.gettingQuotes') +
          (errorDetail ? ` - ${errorDetail}` : ''),
        error: e,
      });
    }
  } finally {
    rateQuotes.value.loading = false;
  }
};

const validateLocation = async (
  location: EmbeddableLocation,
  currentLocation: LocationKey,
): Promise<boolean> => {
  try {
    isLoading.value = true;
    const result = await api.shippingPoint.validateAddress(
      location.toAddress(),
    );

    if (result.errors?.length) {
      addressValidationQueue.value.push({
        locationKey: currentLocation,
        validationResult: result,
      });
      return false;
    } else {
      hasBeenValidated.value[currentLocation] = true;
      return true;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return false;
    }
    console.error('Error validating address', e);
    return true;
  } finally {
    isLoading.value = false;
  }
};

const processNextInQueue = () => {
  if (!addressValidationQueue.value.length) {
    addressValidation.value.showModal = false;
    return;
  }

  const next = addressValidationQueue.value.shift()!;
  addressValidation.value.currentLocation = next.locationKey;
  addressValidation.value.validationResult = next.validationResult;

  if (addressValidation.value.showModal) {
    addressValidation.value.showModal = false;
    nextTick(() => {
      addressValidation.value.showModal = true;
    });
  } else {
    addressValidation.value.showModal = true;
  }
};

const onAcceptCorrection = () => {
  const currentLocation = addressValidation.value.currentLocation;
  if (!request.value[currentLocation]) {
    return;
  }

  const location = request.value[currentLocation] as QuickQuoteShippingPoint;
  location.location = EmbeddableLocation.fromAddress(
    addressValidation.value.validationResult.correctedAddress?.address,
    location.location?.locationName,
  );
  hasAttemptedResolution.value[addressValidation.value.currentLocation] = true;
  hasBeenValidated.value[currentLocation] = true;

  const hasMore = addressValidationQueue.value.length > 0;
  processNextInQueue();

  if (!hasMore) {
    onGetRates(true);
  }
};

const onKeepOriginalAddress = () => {
  const currentLocation = addressValidation.value.currentLocation;
  hasBeenValidated.value[currentLocation] = true;
  hasAttemptedResolution.value[addressValidation.value.currentLocation] = true;

  const hasMore = addressValidationQueue.value.length > 0;
  processNextInQueue();

  if (!hasMore) {
    onGetRates(true);
  }
};

const onClientChange = (newClient: DomainReference) => {
  selectedClient.value = newClient.id
    ? clientTreeMap.value.get(newClient.id)
    : undefined;
};

const updateSelectedClient = (currentDomain: DomainReference | undefined) => {
  selectedClient.value =
    currentDomain?.domainType === DomainType.Client && currentDomain.id
      ? clientTreeMap.value.get(currentDomain.id)
      : undefined;

  isChangeDomainModalVisible.value = !selectedClient.value;
};

watch(selectedDomain, (newVal: DomainReference | undefined) => {
  updateSelectedClient(newVal);
});

watch(
  selectedClient,
  (newVal: DomainTree | undefined) => {
    if (newVal) {
      request.value.owner = newVal?.asDomainReference();
      setParentEntity(request);

      addMessage({
        id: OWNING_CLIENT_MESSAGE_KEY,
        severity: 'info',
        text: t('common.assignedToParam', { param: newVal.name }),
        iconSlot: () =>
          h(ClientAvatar, {
            name: newVal.name,
            logo: newVal.branding?.avatar?.signedUrl,
          }),
        onClick: () => (isChangeDomainModalVisible.value = true),
      });
    }
  },
  { immediate: true },
);

watch(isChangeDomainModalVisible, (newVal: boolean, oldVal: boolean) => {
  if (oldVal && !newVal && !selectedClient.value) {
    router.back();
  }
});

watch(
  request,
  (newVal: QuickQuoteRequest, oldVal: QuickQuoteRequest) => {
    if (!isEqual(newVal, oldVal)) {
      rateQuotes.value.data = undefined;
      v$.value.$reset();
    }
  },
  { deep: true },
);

watch(
  () => request.value.origin,
  () => {
    hasBeenValidated.value.origin = false;
    hasAttemptedResolution.value.origin = false;
  },
  { deep: true },
);

watch(
  () => request.value.destination,
  () => {
    hasBeenValidated.value.destination = false;
    hasAttemptedResolution.value.destination = false;
  },
  { deep: true },
);

onMounted(async () => {
  await loadClients();
  updateSelectedClient(selectedDomain.value);
});

onBeforeUnmount(() => {
  clearParentEntity();
  removeMessage(OWNING_CLIENT_MESSAGE_KEY);
});
</script>

<style>
.quick-quote {
  .detail-section {
    padding: 0;
    border: none;

    & + .detail-section {
      margin: var(--sp-6) 0 0;
      border-top: none;
    }
  }

  .quick-quote-accessorials {
    --p-card-body-padding: var(--sp-3) var(--sp-6);
  }

  .content-secondary {
    margin-top: var(--sp-8);
  }

  .p-blockui-mask.p-overlay-mask {
    background-color: rgb(from var(--p-surface-100) r g b / 0.65);
    /*
    There's a bug with the PrimeVue component where the animation class isn't
    removed so it prevents the style above from being applied
    */
    animation: none;
  }
}
</style>
